export function parseTemplateComponents(raw) {
  if (Array.isArray(raw)) {
    return raw;
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export function isAuthenticationTemplate(components) {
  const parsed = parseTemplateComponents(components);
  return parsed.some((component) => {
    if (!component || typeof component !== "object") return false;
    if (String(component.type ?? "").toUpperCase() !== "BUTTONS") return false;
    return Array.isArray(component.buttons) && component.buttons.some((button) => button?.type === "OTP");
  });
}

function readAuthTemplateOptions(components) {
  const parsed = parseTemplateComponents(components);
  const body = parsed.find(
    (component) => String(component?.type ?? "").toUpperCase() === "BODY",
  );
  const footer = parsed.find(
    (component) => String(component?.type ?? "").toUpperCase() === "FOOTER",
  );
  const buttons = parsed.find(
    (component) => String(component?.type ?? "").toUpperCase() === "BUTTONS",
  );
  const otpButton = buttons?.buttons?.find((button) => button?.type === "OTP");

  return {
    addSecurityRecommendation: body?.add_security_recommendation === true,
    codeExpirationMinutes:
      typeof footer?.code_expiration_minutes === "number"
        ? footer.code_expiration_minutes
        : null,
    buttonText: otpButton?.text ?? "",
  };
}

function buildAuthPreviewPartsFromComponents(components, values) {
  const options = readAuthTemplateOptions(components);
  const code = values[1] != null && String(values[1]).trim() !== "" ? String(values[1]) : "{{1}}";
  const language = "es";
  const copy = {
    es: {
      body: `*${code}* es tu código de verificación.`,
      security: "Por tu seguridad, no compartas este código.",
      expires: `Este código expira en ${options.codeExpirationMinutes ?? 10} minutos.`,
    },
    en: {
      body: `*${code}* is your verification code.`,
      security: "For your security, do not share this code.",
      expires: `This code expires in ${options.codeExpirationMinutes ?? 10} minutes.`,
    },
  }[language];

  const parts = [{ kind: "body", text: copy.body }];
  if (options.addSecurityRecommendation) {
    parts.push({ kind: "body", text: copy.security });
  }
  if (options.codeExpirationMinutes) {
    parts.push({ kind: "footer", text: copy.expires });
  }
  if (options.buttonText) {
    parts.push({
      kind: "buttons",
      buttons: [{ text: options.buttonText, type: "OTP" }],
    });
  }
  return parts;
}

export function extractTemplateVariableIndexes(text) {
  if (typeof text !== "string" || !text) {
    return [];
  }
  const indexes = new Set();
  const matches = text.matchAll(/\{\{(\d+)\}\}/g);
  for (const match of matches) {
    indexes.add(Number(match[1]));
  }
  return [...indexes].sort((a, b) => a - b);
}

export function extractVariablesFromComponents(components) {
  if (isAuthenticationTemplate(components)) {
    return [1];
  }

  const parsed = parseTemplateComponents(components);
  const indexes = new Set();

  for (const component of parsed) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type !== "HEADER" && typeof component.text === "string") {
      extractTemplateVariableIndexes(component.text).forEach((idx) => indexes.add(idx));
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      for (const button of component.buttons) {
        if (typeof button?.url === "string") {
          extractTemplateVariableIndexes(button.url).forEach((idx) => indexes.add(idx));
        }
      }
    }
  }

  return [...indexes].sort((a, b) => a - b);
}

export function applyTemplateVariables(text, values) {
  if (typeof text !== "string" || !text) {
    return "";
  }

  return text.replace(/\{\{(\d+)\}\}/g, (_, rawIndex) => {
    const index = Number(rawIndex);
    const value = values[index];
    if (value == null || String(value).trim() === "") {
      return `{{${index}}}`;
    }
    return String(value);
  });
}

export function buildTemplatePreviewParts(components, values) {
  if (isAuthenticationTemplate(components)) {
    return buildAuthPreviewPartsFromComponents(components, values);
  }

  const parsed = parseTemplateComponents(components);
  const parts = [];

  for (const component of parsed) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type === "HEADER") {
      if (component.format === "TEXT" && component.text) {
        parts.push({
          kind: "header",
          text: applyTemplateVariables(component.text, values),
        });
      } else if (component.format && component.format !== "TEXT") {
        const exampleUrl = component.example?.header_url?.[0];
        parts.push({
          kind: "media",
          label: String(component.format).toLowerCase(),
          previewUrl:
            component.format === "IMAGE" && typeof exampleUrl === "string"
              ? exampleUrl
              : undefined,
        });
      }
    }

    if (type === "BODY" && component.text) {
      parts.push({
        kind: "body",
        text: applyTemplateVariables(component.text, values),
      });
    }

    if (type === "FOOTER" && component.text) {
      parts.push({
        kind: "footer",
        text: applyTemplateVariables(component.text, values),
      });
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons) && component.buttons.length > 0) {
      parts.push({
        kind: "buttons",
        buttons: component.buttons.map((button) => ({
          text: button?.text ?? "",
          type: button?.type ?? "QUICK_REPLY",
        })),
      });
    }
  }

  if (parts.length === 0) {
    return [{ kind: "body", text: "" }];
  }

  return parts;
}

export function buildTemplatePreviewText(components, values) {
  return buildTemplatePreviewParts(components, values)
    .filter((part) => part.kind === "header" || part.kind === "body" || part.kind === "footer")
    .map((part) => part.text)
    .filter(Boolean)
    .join("\n\n");
}

export function buildMetaTemplateComponents(components, values) {
  if (isAuthenticationTemplate(components)) {
    const code = values[1];
    if (code == null || String(code).trim() === "") {
      return undefined;
    }
    return [
      {
        type: "body",
        parameters: [{ type: "text", text: String(code) }],
      },
    ];
  }

  const parsed = parseTemplateComponents(components);
  const payload = [];

  for (const component of parsed) {
    if (!component || typeof component !== "object") continue;
    const type = String(component.type ?? "").toUpperCase();

    if (type === "BODY" && typeof component.text === "string") {
      const indexes = extractTemplateVariableIndexes(component.text);
      if (indexes.length > 0) {
        payload.push({
          type: "body",
          parameters: indexes.map((index) => ({
            type: "text",
            text: String(values[index] ?? ""),
          })),
        });
      }
    }

    if (type === "HEADER" && component.format === "TEXT" && typeof component.text === "string") {
      const indexes = extractTemplateVariableIndexes(component.text);
      if (indexes.length > 0) {
        payload.push({
          type: "header",
          parameters: indexes.map((index) => ({
            type: "text",
            text: String(values[index] ?? ""),
          })),
        });
      }
    }

    if (type === "BUTTONS" && Array.isArray(component.buttons)) {
      component.buttons.forEach((button, buttonIndex) => {
        if (button?.type !== "URL" || typeof button.url !== "string") {
          return;
        }
        const indexes = extractTemplateVariableIndexes(button.url);
        if (indexes.length === 0) {
          return;
        }
        payload.push({
          type: "button",
          sub_type: "url",
          index: String(buttonIndex),
          parameters: indexes.map((index) => ({
            type: "text",
            text: String(values[index] ?? ""),
          })),
        });
      });
    }
  }

  return payload.length > 0 ? payload : undefined;
}

export function areTemplateVariablesFilled(indexes, values) {
  return indexes.every((index) => {
    const value = values[index];
    return value != null && String(value).trim().length > 0;
  });
}
